import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, Globe, MessageCircle, PhoneCall } from 'lucide-react';
import { suspendedSlugs } from '../data/suspendedPageContent';
import { whatsappNumber, phoneNumber, images, contactByLanguage } from '../data/mock';

// Google Colors
const googleColors = {
  blue: '#4285F4',
  red: '#EA4335',
  yellow: '#FBBC04',
  green: '#34A853',
};

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const languages = [
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
  ];

  // Call button text by language
  const callText = {
    es: 'Llamar',
    fr: 'Appeler',
    en: 'Call',
  };

  // URL paths by language
  const urlPaths = {
    es: { services: 'servicios', webCreation: 'creacion-web', contact: 'contacto' },
    fr: { services: 'services', webCreation: 'creation-web', contact: 'contact' },
    en: { services: 'services', webCreation: 'web-creation', contact: 'contact' },
  };

  const paths = urlPaths[language] || urlPaths.es;

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    const currentPath = location.pathname;
    // Get path without language prefix
    const pathWithoutLang = currentPath.replace(/^\/(es|fr|en)/, '') || '/';
    
    // Map old URL structure to new for the target language
    const newPaths = urlPaths[lang];
    let newPath = pathWithoutLang;
    
    // Convert path to target language
    if (['/cuenta-google-ads-suspendida', '/compte-google-ads-suspendu', '/google-ads-account-suspended'].includes(pathWithoutLang)) {
      newPath = suspendedSlugs[lang].replace(/^\/(es|fr|en)/, '');
    } else if (pathWithoutLang.includes('servicios') || pathWithoutLang.includes('services')) {
      newPath = `/${newPaths.services}`;
    } else if (pathWithoutLang.includes('creacion-web') || pathWithoutLang.includes('creation-web') || pathWithoutLang.includes('web-creation')) {
      newPath = `/${newPaths.webCreation}`;
    } else if (pathWithoutLang.includes('contacto') || pathWithoutLang.includes('contact')) {
      newPath = `/${newPaths.contact}`;
    } else if (pathWithoutLang.includes('privacidad') || pathWithoutLang.includes('confidentialite') || pathWithoutLang.includes('privacy')) {
      newPath = lang === 'fr' ? '/confidentialite' : lang === 'en' ? '/privacy' : '/privacidad';
    }
    
    navigate(`/${lang}${newPath === '/' ? '' : newPath}`);
  };

  const getNavLinks = () => [
    { path: `/${language}`, label: t.nav.home },
    { path: `/${language}/${paths.services}`, label: t.nav.services },
    { path: `/${language}/compliance`, label: t.nav.compliance },
    { path: `/${language}/${paths.webCreation}`, label: t.nav.webCreation },
    { path: `/${language}/${paths.contact}`, label: t.nav.contact },
  ];

  // Get contact info based on current language
  const currentContact = contactByLanguage[language] || contactByLanguage.es;

  const openWhatsApp = () => {
    // GTM Conversion Event - WhatsApp Click
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'click_whatsapp',
        click_location: 'header',
        language: language
      });
    }
    window.open(`https://wa.me/${currentContact.whatsapp}`, '_blank');
  };

  const callPhone = () => {
    // GTM Conversion Event - Call Click
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'click_call',
        click_location: 'header',
        phone_number: currentContact.phone,
        language: language
      });
    }
    window.location.href = `tel:${currentContact.phone.replace(/\s/g, '')}`;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to={`/${language}`} className="flex items-center group">
            <img 
              src={images.agencyLogo} 
              alt="Agencia PPC" 
              className="h-10 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {getNavLinks().map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 rounded-lg hover:bg-slate-50 transition-all duration-200"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-2">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center space-x-1 text-slate-600 hover:text-slate-800">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase text-xs font-semibold">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-blue-50' : ''}`}
                    style={{ color: language === lang.code ? googleColors.blue : undefined }}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Phone Button - Desktop */}
            <Button
              onClick={callPhone}
              className="hidden md:flex items-center space-x-2 text-white shadow-md hover:shadow-lg transition-all duration-300"
              size="sm"
              style={{ backgroundColor: googleColors.green }}
            >
              <PhoneCall className="w-4 h-4" />
              <span className="hidden lg:inline">{callText[language] || 'Llamar'}</span>
            </Button>

            {/* WhatsApp Button - Desktop */}
            <Button
              onClick={openWhatsApp}
              className="hidden sm:flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 text-white shadow-md hover:shadow-lg transition-all duration-300"
              size="sm"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp</span>
            </Button>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 animate-in slide-in-from-top-2 duration-200">
            <nav className="flex flex-col space-y-1">
              {getNavLinks().map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex gap-2 mx-4 mt-2">
                <Button
                  onClick={callPhone}
                  className="flex-1 text-white"
                  style={{ backgroundColor: googleColors.green }}
                >
                  <PhoneCall className="w-4 h-4 mr-2" />
                  {callText[language] || 'Llamar'}
                </Button>
                <Button
                  onClick={openWhatsApp}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
